import { useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from '@mint-lib/tw-components';

import type { GlossaryItem, GlossaryType } from '../../api/types.js';
import { useImportHubV2 } from '../../hooks/ff/useImportHubV2.js';
import { useAddGlossaryItem } from '../../queries/index.js';
import { Loader } from '../atoms/Loader.jsx';
import { ChannelsGlossaryListV1 } from './ChannelsGlossaryListV1.jsx';
import { ChannelsGlossaryListV2 } from './ChannelsGlossaryListV2.jsx';
import { PlatformsGlossaryListV1 } from './PlatformsGlossaryListV1.jsx';
import { PlatformsGlossaryListV2 } from './PlatformsGlossaryListV2.jsx';

const GlossaryListsV1 = {
  platform: PlatformsGlossaryListV1,
  channel: ChannelsGlossaryListV1,
} as const;

const GlossaryListsV2 = {
  platform: PlatformsGlossaryListV2,
  channel: ChannelsGlossaryListV2,
} as const;

type AddGlossariesItemDialogProps = {
  isLoading: boolean;
  glossaryType: GlossaryType;
  glossaryItems: GlossaryItem[];
  onClose: () => void;
};

export const AddGlossariesItemDialog = ({
  isLoading,
  glossaryType,
  glossaryItems,
  onClose,
}: AddGlossariesItemDialogProps) => {
  const { t } = useTranslation('@myn/import-hub');
  const isImportHubV2 = useImportHubV2();

  const [itemSelection, setItemSelection] = useState<{
    [name: string]: boolean;
  }>({});
  const selectedItems = Object.keys(itemSelection).filter(
    (name) => itemSelection[name],
  );
  const hasSelectedItem = Boolean(selectedItems.length);
  const handleSelectItem = (id: string) => () => {
    setItemSelection((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const { mutateAsync: addGlossaryItem } = useAddGlossaryItem();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await Promise.all(
        selectedItems.map((name) => addGlossaryItem({ name, glossaryType })),
      );
      onClose();
    } catch {
      /* empty */
    } finally {
      setIsSubmitting(false);
    }
  };

  const GlossaryListComponent = isImportHubV2
    ? GlossaryListsV2[glossaryType]
    : GlossaryListsV1[glossaryType];

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot size="m">
          <DialogHeader>
            <DialogTitle>
              {glossaryType === 'platform'
                ? t('Add platform to Glossary')
                : t('Add channel to Glossary')}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {glossaryType === 'platform'
                ? t('Select platforms to add to your glossary.')
                : t('Select channels to add to your glossary.')}
            </DialogDescription>
          </DialogHeader>
          <DialogContent className="h-[400px] max-h-[400px] flex-col overflow-auto">
            {isLoading ? (
              <Loader />
            ) : (
              <GlossaryListComponent
                glossaries={glossaryItems}
                selectedItems={selectedItems}
                onItemSelect={handleSelectItem}
              />
            )}
          </DialogContent>
          <DialogFooter>
            <Button
              variant="text"
              size="large"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t('Cancel')}
            </Button>
            <Button
              size="large"
              onClick={handleSubmit}
              disabled={!hasSelectedItem || isSubmitting}
            >
              {t('Confirm')}
            </Button>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};
